import {  internal, template, AttributeMarshallingMixin, ReactiveElement } from '../vendor/elix.js';

const arrow = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
  <g transform="translate(-3 -3)">
    <path d="M14.59,6,16,7.41,11.42,12,16,16.59,14.59,18l-6-6Z" transform="translate(2.41 3)"/>
  </g>
</svg>`;

class TpPagination extends AttributeMarshallingMixin(ReactiveElement) {

  get currentPage() {
    return this[internal.state].currentPage;
  }

  set currentPage(currentPage) {
    currentPage = parseInt(currentPage);
    this[internal.setState]({ currentPage });
  }

  get totalPages() {
    return this[internal.state].totalPages;
  }

  set totalPages(totalPages) {
    totalPages = parseInt(totalPages);
    this[internal.setState]({ totalPages });
  }

  [internal.componentDidMount]() {
    super[internal.componentDidMount]();
    this[internal.ids].previous.addEventListener('click', this.onClickPrevious.bind(this));
    this[internal.ids].next.addEventListener('click', this.onClickNext.bind(this));
    this[internal.ids].pages.addEventListener('click', this.onClickPages.bind(this));
  }

  get [internal.defaultState]() {
    return Object.assign(
      {},
      super[internal.defaultState],
      { currentPage: 1, totalPages: 1 }
    );
  }

  [internal.render](changed) {
    super[internal.render](changed);
    this.renderPreviousArrow();
    this.renderNextArrow();
    this.renderPageNumbers();
  }

  renderPreviousArrow() {
    const previous = this[internal.ids].previous;
    if (this.currentPage > 1) {
      previous.classList.remove('disabled');
    } else {
      previous.classList.add('disabled');
    }
  }

  renderNextArrow() {
    const next = this[internal.ids].next;
    if (this.currentPage < this.totalPages) {
      next.classList.remove('disabled');
    } else {
      next.classList.add('disabled');
    }
  }

  renderPageNumbers() {
    const pagesContainer = this[internal.ids].pages;
    while (pagesContainer.firstElementChild) {
      pagesContainer.firstElementChild.remove();
    }

    let pageNumbers;

    if (this.totalPages <= 7) {
      pageNumbers = this.buildAllPageNumbers();
    } else if (this.currentPage <= 5) {
      pageNumbers = this.buildPageNumbersWithEndTruncated();
    } else if (this.currentPage >= this.totalPages - 4) {
      pageNumbers = this.buildPageNumbersWithBeginningTruncated();
    } else {
      pageNumbers = this.buildPageNumbersWithDoubleTruncation();
    }

    pageNumbers.forEach(number => pagesContainer.appendChild(number));
  }

  buildAllPageNumbers() {
    const links = [];
    for(let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
      links.push(this.buildPageNumberLink(pageNumber));
    }
    return links;
  }

  buildPageNumbersWithEndTruncated() {
    const links = [];
    for(let pageNumber = 1; pageNumber <= 5; pageNumber++) {
      links.push(this.buildPageNumberLink(pageNumber));
    }
    links.push(this.buildEllipsis());
    links.push(this.buildPageNumberLink(this.totalPages));
    return links;
  }

  buildPageNumbersWithBeginningTruncated() {
    const links = [];
    links.push(this.buildPageNumberLink(1));
    links.push(this.buildEllipsis());
    for(let pageNumber = this.totalPages - 4; pageNumber <= this.totalPages; pageNumber++) {
      links.push(this.buildPageNumberLink(pageNumber));
    }
    return links;
  }

  buildPageNumbersWithDoubleTruncation() {
    const links = [];
    links.push(this.buildPageNumberLink(1));
    links.push(this.buildEllipsis());
    for(let pageNumber = this.currentPage - 1; pageNumber <= this.currentPage + 1; pageNumber++) {
      links.push(this.buildPageNumberLink(pageNumber));
    }
    links.push(this.buildEllipsis());
    links.push(this.buildPageNumberLink(this.totalPages));
    return links;
  }

  buildPageNumberLink(pageNumber) {
    const link = document.createElement('a');
    link.classList.add('page-number');
    link.classList.add('page-control');
    link.dataset.page = pageNumber;
    if(pageNumber === this.currentPage) {
      link.classList.add('selected');
    }
    link.textContent = pageNumber;
    return link;
  }

  buildEllipsis() {
    const span = document.createElement('span');
    span.classList.add('page-number');
    span.classList.add('page-control');
    span.classList.add('disabled');
    span.textContent = '...';
    return span;
  }

  onClickPrevious() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.dispatchChange();
    }
  }

  onClickNext() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.dispatchChange();
    }
  }

  onClickPages(e) {
    if (e.target.dataset.hasOwnProperty('page')) {
      this.currentPage = e.target.dataset.page;
      this.dispatchChange();
    }
  }

  dispatchChange() {
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: true }));
  }

  get [internal.template]() {
    return template.html`

      <div id='previous' class='previous-arrow page-control pagination-arrow'>${arrow}</div>

      <div id='pages' class='page-numbers'></div>

      <div id='next' class='next-arrow page-control pagination-arrow'>${arrow}</div>
      
      <style>

        :host {
          display: inline-flex;
          user-select: none;
        }

        .pagination-arrow, .page-numbers {
          display: inline-block;
        }

        .page-control {
          align-items: center;
          background-color: var(--tp-pagination-background-color);
          border-radius: 50%;
          display: inline-flex;
          height: 32px;
          justify-content: center;
          text-align: center;
          width: 32px;
        }

        .page-control:not(.selected):not(.disabled):hover,
        .page-control:not(.selected):not(.disabled):active {
          background-color: var(--tp-pagination-background-color-hover);
          cursor: pointer;
        }

        .previous-arrow {
          margin-right: 2px;
        }

        .next-arrow {
          margin-left: 2px;
          transform: rotate(180deg);
        }

        .pagination-arrow svg {
          fill: var(--tp-pagination-arrow-color);
        }

        .pagination-arrow.disabled svg {
          fill: var(--tp-pagination-arrow-color-disabled);
        }

        .page-number {
          color: var(--tp-pagination-color);
          font-family: var(--tp-pagination-font-family);
          font-size: var(--tp-pagination-font-size);
          margin: 0px 2px;
        }

        .page-number.selected {
          background-color: var(--tp-pagination-background-color-selected);
          box-shadow: var(--tp-box-shadow);
          color: var(--tp-pagination-color-selected);
        }

      </style>
    `;
  }
}

export default TpPagination;
